import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  createEventForDoctor,
  fetchEventsByDoctor,
  fetchEventsByRange,
  fetchEventsByDay,
    deleteEvent,          // ← ajouter

} from "../redux/thunks/eventsThunks";

import {
  selectEventsList,
  selectCurrentEvent,
  selectEventsLoading,
  selectEventsError,
  selectEventsLastFilter,
} from "../redux/selectors/eventsSelectors";

import { getCurrentDoctor } from "../../authDoctor/redux/thunks/doctorsThunks";

/* ─── Utilitaires ─────────────────────────────────────────────────────────── */
const buildIsoDateTime = (date, time) => {
  if (!date || !time) return null;
  // time peut être "09:00" ou "09:00:00" — on garde HH:mm
  const hhmm = time.substring(0, 5);
  return `${date}T${hhmm}:00.000Z`;
};

const mapReasonToType = (reason) => {
  if (!reason) return "event";
  switch (reason) {
    case "consultation":
    case "followup":
    case "checkup":
      return "appointment";
    case "surgery":
      return "operational";
    default:
      return "event";
  }
};

/* ─── Hook ────────────────────────────────────────────────────────────────── */
export const useEvents = (options = {}) => {
  const {
    doctorIdOverride = null,
    disableDoctorBootstrap = false,
  } = options;

  const dispatch = useDispatch();

  // Events Redux
  const events      = useSelector(selectEventsList) || [];
  const currentEvent = useSelector(selectCurrentEvent);
  const loading     = useSelector(selectEventsLoading);
  const error       = useSelector(selectEventsError);
  const lastFilter  = useSelector(selectEventsLastFilter);

  // Doctor Redux
  const {
    user: currentDoctor,
    loading: doctorLoading,
    error: doctorError,
  } = useSelector((state) => state.doctor || {});

  // Au mount : charger le docteur courant
  useEffect(() => {
    if (disableDoctorBootstrap || doctorIdOverride) return;
    dispatch(getCurrentDoctor());
  }, [dispatch, disableDoctorBootstrap, doctorIdOverride]);

  const doctorId =
    doctorIdOverride ||
    currentDoctor?.id ||
    currentDoctor?.pgId ||
    currentDoctor?._id ||
    null;

  // Quand doctorId arrive → charger TOUS ses events (liste complète)
  useEffect(() => {
    if (!doctorId) return;
    dispatch(fetchEventsByDoctor(doctorId));
  }, [dispatch, doctorId]);

  /* ============================================================
     CREATE DOCTOR EVENT
     ─ FIX : après création on recharge TOUS les events du docteur
       au lieu de fetchByDay/fetchByRange qui écrasent la liste
       avec un sous-ensemble et font disparaître les autres events.
  ============================================================ */
  const createDoctorEvent = useCallback(
    async (schedulePayload) => {
      if (!doctorId) {
        console.error("❌ doctorId introuvable.");
        throw new Error("Doctor not authenticated");
      }

      const {
        name,
        fromDate,
        toDate,
        startTime,
        endTime,
        reason,
        description,
        location,
      } = schedulePayload || {};

      if (!fromDate || !startTime || !endTime) {
        throw new Error("fromDate, startTime et endTime sont obligatoires.");
      }

      const startTimeISO = buildIsoDateTime(fromDate, startTime);
      const endTimeISO   = buildIsoDateTime(toDate || fromDate, endTime);

      const dto = {
        title:       name || "New event",
        description: description || (reason ? `Event about ${reason.toLowerCase()}` : "Event"),
        type:        mapReasonToType(reason),
        startTime:   startTimeISO,
        endTime:     endTimeISO,
        location:    location || "Cabinet Médical Bloc A",
        participants: [],
      };

      // 1️⃣ Créer l'event → le slice push dans s.list via fulfilled
      const created = await dispatch(
        createEventForDoctor({ doctorId, eventData: dto })
      ).unwrap();

      // 2️⃣ ✅ FIX : recharger la liste COMPLÈTE du docteur
      //    (pas fetchByDay/fetchByRange qui remplacerait s.list
      //     avec seulement les events de cette plage)
      try {
        await dispatch(fetchEventsByDoctor(doctorId)).unwrap();
      } catch (e) {
        console.warn("⚠️ Re-fetch après création échoué :", e);
        // Le push dans fulfilled garde quand même l'event dans la liste
      }

      return created;
    },
    [dispatch, doctorId]
  );

  /* ─── Loaders utilitaires ─────────────────────────────────────────────── */
  const loadEventsForDoctor = useCallback(() => {
    if (!doctorId) return Promise.resolve([]);
    return dispatch(fetchEventsByDoctor(doctorId)).unwrap();
  }, [dispatch, doctorId]);

  const loadEventsByRange = useCallback(
    ({ startDate, endDate }) =>
      dispatch(fetchEventsByRange({ startDate, endDate })).unwrap(),
    [dispatch]
  );

  /* ─── Computed ────────────────────────────────────────────────────────── */
  const upcomingEvents = useMemo(() => {
    const now = new Date();
    return (events || [])
      .filter((e) => !!e?.startTime)
      .map((e) => ({ ...e, _start: new Date(e.startTime) }))
      .filter((e) => e._start >= now)
      .sort((a, b) => a._start - b._start)
      .slice(0, 5);
  }, [events]);
  const deleteEventById = useCallback(
  async (id) => {
    if (!id) throw new Error("Event id manquant");
    await dispatch(deleteEvent(id)).unwrap();
    // Le slice filtre déjà la liste via deleteEvent.fulfilled
  },
  [dispatch]
);

  return {
    events,
    currentEvent,
    loading,
    error,
    lastFilter,
    doctorId,
    doctorLoading,
    doctorError,
    createDoctorEvent,
    loadEventsForDoctor,
    loadEventsByRange,
    upcomingEvents,
      deleteEventById,

  };
};

export default useEvents;